// FILE: apps/admin/src/pages/Banners.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Promotional Banners CMS page featuring an HTML5 Canvas 256x168 WebP/PNG image cropper, placement selector, ocean preset themes, scheduling, and live Mini App banner preview.
//   SCOPE: Banners component and Canvas 256x168 cropper.
//   DEPENDS: ../api.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Banners - Admin Promotional Banners CMS with HTML5 Canvas 256x168 cropper
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useRef, useState } from 'react';

import { api, dt } from '../api.js';
import { useToast } from '../toast.js';

interface BannerItem {
  id: string;
  placement: string;
  template: 'compact' | 'featured';
  preset: string;
  eyebrow: string | null;
  title: string;
  subtitle: string | null;
  badge: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  imageDataUrl: string | null;
  dismissible: boolean;
  priority: number;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

const PLACEMENTS = [
  { id: 'stars_top', label: 'Покупка Stars (верх)' },
  { id: 'aquarium_top', label: 'Аквариум Тамагочи (верх)' },
  { id: 'cases_top', label: 'Кейсы (верх)' },
  { id: 'games_top', label: 'Мини-игры (верх)' },
  { id: 'profile_top', label: 'Профиль (верх)' },
] as const;

const PRESETS = [
  { id: 'ocean_cyan', label: 'Океан (Cyan)' },
  { id: 'gold_treasure', label: 'Сокровище (Gold)' },
  { id: 'violet_depths', label: 'Глубина (Violet)' },
  { id: 'coral_sunset', label: 'Коралл (Coral)' },
  { id: 'emerald_kelp', label: 'Изумруд (Emerald)' },
] as const;

// START_CONTRACT: Banners
//   PURPOSE: Render the Promotional Banners CMS with an HTML5 Canvas 256x168 WebP/PNG cropper and live preview.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET/POST/PUT/DELETE /admin/banners
//   LINKS: M-ADMIN
// END_CONTRACT: Banners
export function Banners() {
  // START_BLOCK_BANNERS
  const qc = useQueryClient();
  const toast = useToast();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [placement, setPlacement] = useState('stars_top');
  const [preset, setPreset] = useState('ocean_cyan');
  const [eyebrow, setEyebrow] = useState('АКЦИЯ АКВАРИУМА');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [badge, setBadge] = useState('NEW');
  const [ctaLabel, setCtaLabel] = useState('Открыть');
  const [ctaUrl, setCtaUrl] = useState('');
  const [priority, setPriority] = useState(10);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [sourceImg, setSourceImg] = useState<HTMLImageElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => api.get<{ items: BannerItem[] }>('/admin/banners'),
  });

  const renderCroppedCanvas = (img: HTMLImageElement, scale: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 256;
    canvas.height = 168;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, 256, 168);

    const targetRatio = 256 / 168;
    const srcRatio = img.width / img.height;
    let drawW = 256 * scale;
    let drawH = 168 * scale;
    if (srcRatio > targetRatio) {
      drawW = 168 * srcRatio * scale;
    } else {
      drawH = (256 / srcRatio) * scale;
    }
    const dx = (256 - drawW) / 2;
    const dy = (168 - drawH) / 2;
    ctx.drawImage(img, dx, dy, drawW, drawH);
    const dataUrl = canvas.toDataURL('image/webp', 0.88);
    setImageDataUrl(dataUrl);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setSourceImg(img);
        setZoom(1);
        renderCroppedCanvas(img, 1);
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const createMut = useMutation({
    mutationFn: () =>
      api.post('/admin/banners', {
        placement,
        preset,
        eyebrow: eyebrow.trim() || null,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        badge: badge.trim() || null,
        ctaLabel: ctaLabel.trim() || null,
        ctaUrl: ctaUrl.trim() || null,
        imageDataUrl,
        priority: Number(priority),
        active: true,
      }),
    onSuccess: () => {
      toast('Баннер опубликован в Mini App');
      setTitle('');
      setSubtitle('');
      void qc.invalidateQueries({ queryKey: ['admin-banners'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка создания баннера'),
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.put(`/admin/banners/${id}`, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-banners'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/banners/${id}`),
    onSuccess: () => {
      toast('Баннер удалён');
      void qc.invalidateQueries({ queryKey: ['admin-banners'] });
    },
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Промо-баннеры Mini App (CMS + Canvas 256×168)</h1>
          <div className="page-subtitle">
            Управление рекламными слотами на экранах покупки Stars, Аквариума, Кейсов и Игр
          </div>
        </div>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 22, alignItems: 'start' }}>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Создать новый баннер</h2>
          <div className="grid cols-2" style={{ marginBottom: 10 }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Слот размещения (Placement)</label>
              <select
                style={{ width: '100%' }}
                value={placement}
                onChange={(e) => setPlacement(e.target.value)}
              >
                {PLACEMENTS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Цветовая тема (Preset)</label>
              <select
                style={{ width: '100%' }}
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
              >
                {PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid cols-2" style={{ marginBottom: 10 }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Надпись над заголовком (Eyebrow)</label>
              <input style={{ width: '100%' }} value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Бейдж (Badge)</label>
              <input style={{ width: '100%' }} value={badge} onChange={(e) => setBadge(e.target.value)} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label className="muted" style={{ fontSize: 12 }}>Заголовок баннера *</label>
            <input
              style={{ width: '100%' }}
              placeholder="Бонус +15% при покупке от 500 Stars"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label className="muted" style={{ fontSize: 12 }}>Подзаголовок</label>
            <input
              style={{ width: '100%' }}
              placeholder="Успей покормить рыбку и получить двойной опыт!"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </div>

          <div className="grid cols-3" style={{ marginBottom: 12 }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Текст кнопки CTA</label>
              <input style={{ width: '100%' }} value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Ссылка CTA (URL)</label>
              <input style={{ width: '100%' }} placeholder="https://t.me/..." value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Приоритет</label>
              <input type="number" style={{ width: '100%' }} value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
            </div>
          </div>

          {/* HTML5 Canvas 256x168 Cropper */}
          <div className="card" style={{ background: 'var(--bg-2)', marginBottom: 14 }}>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Кадрирование иллюстрации 256×168 (WebP/PNG)</strong>
              <input
                type="file"
                accept="image/*"
                style={{ maxWidth: '100%' }}
                onChange={handleFileChange}
              />
            </div>
            <div className="row" style={{ gap: 14 }}>
              <canvas
                ref={canvasRef}
                width={256}
                height={168}
                className="cropper-canvas"
                style={{ width: 160, height: 105, maxWidth: '100%' }}
              />
              {sourceImg && (
                <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                  <label className="muted" style={{ fontSize: 12 }}>Масштаб (Zoom): ×{zoom.toFixed(2)}</label>
                  <input
                    type="range"
                    min="1"
                    max="2.5"
                    step="0.05"
                    value={zoom}
                    style={{ width: '100%' }}
                    onChange={(e) => {
                      const nextZoom = Number(e.target.value);
                      setZoom(nextZoom);
                      renderCroppedCanvas(sourceImg, nextZoom);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn primary"
              disabled={!title.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Публикация…' : 'Опубликовать баннер'}
            </button>
          </div>
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Живое превью в Mini App</h2>
          <div
            className="card"
            style={{
              background:
                preset === 'gold_treasure'
                  ? 'linear-gradient(135deg, rgba(245,158,11,0.25), rgba(15,23,42,0.9))'
                  : preset === 'violet_depths'
                    ? 'linear-gradient(135deg, rgba(139,92,246,0.28), rgba(15,23,42,0.9))'
                    : 'linear-gradient(135deg, rgba(6,182,212,0.25), rgba(15,23,42,0.9))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 14,
              marginBottom: 20,
            }}
          >
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <div className="row" style={{ gap: 6, marginBottom: 4 }}>
                {eyebrow && (
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color: '#38bdf8' }}>
                    {eyebrow}
                  </span>
                )}
                {badge && <span className="chip info">{badge}</span>}
              </div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                {title || 'Заголовок вашего промо-баннера'}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                {subtitle || 'Подзаголовок акции в шапке экрана Mini App'}
              </div>
              {ctaLabel && (
                <button type="button" className="btn sm primary" style={{ marginTop: 10 }}>
                  {ctaLabel} →
                </button>
              )}
            </div>
            {imageDataUrl && (
              <img
                src={imageDataUrl}
                alt="preview"
                style={{ width: 112, height: 74, borderRadius: 10, objectFit: 'cover' }}
              />
            )}
          </div>

          <h2>Активные и запланированные баннеры</h2>
          {isLoading ? (
            <div className="muted">Загрузка…</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Баннер</th>
                    <th>Слот</th>
                    <th>Статус</th>
                    <th>Создан</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items ?? []).map((b) => (
                    <tr key={b.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{b.title}</div>
                        <div className="muted" style={{ fontSize: 11 }}>{b.subtitle}</div>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>{b.placement}</td>
                      <td>
                        <button
                          type="button"
                          className={`btn sm ${b.active ? 'success' : 'ghost'}`}
                          onClick={() => toggleActiveMut.mutate({ id: b.id, active: !b.active })}
                        >
                          {b.active ? 'Активен' : 'Выкл'}
                        </button>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{dt(b.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn sm danger"
                          onClick={() => deleteMut.mutate(b.id)}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!data?.items?.length && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                        Баннеров пока нет
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
  // END_BLOCK_BANNERS
}
